/*
  DIHSAN COLLECTION FILTERS v2: the phone filter drawer's live counts, cancel-on-close and history
  (2026-10-02, nav-rebuild/filters-v2/DESIGN.md "Apply model", Rishi's decisions 3:51 PM). Loaded on collection and search pages
  only (layout/theme.liquid). Every listener is delegated on document and attached once, so it survives the theme's section swaps.

  · COUNTS: GET <path>?<pending filters>&filter.v.availability=1&view=rfacets (templates/collection.rfacets.liquid /
    search.rfacets.liquid) → {"count", "filters":[{"p","v":[[value,count,active]…]}…]}. Always with In stock: without it the size
    counts are meaningless (Dresses 287/286/281 against 222/206/71). Prefetched when the page goes idle and on pointerdown/focus of
    FILTER; after each tick 250ms (600ms while typing a price), the earlier request aborted, the last 20 answers cached. The counts
    are written into the open drawer by input name + value; the grid behind the drawer is NEVER swapped while it is open (a swap
    would create a second #facets-drawer-form while the open drawer sits at the end of <body>).
  · ONLY 'SHOW n PRODUCTS' APPLIES (Rishi: "X cancels"). X, tapping outside, Escape and Back/swipe-back restore every input to
    the state the page was drawn with (defaultChecked / defaultValue). ⚠️ NEVER form.reset(): the theme's reset listener turns a
    reset into "clear every filter" and applies it.
  · HISTORY: opening the drawer pushes an entry, so Back closes it (= cancel). SHOW closes the drawer and THEN fires the theme's
    own facet:update (one section fetch); theme.js now pushes each filter state (two commented edits there), and the popstate
    handler below swaps the grid back as Back steps through filter states.
  · Also: the drawer's Clear all (pending picks only, sort kept), designer search + 'View all n', closed-section pick summaries,
    zero values (size boxes disabled in place, other values left out when their section opens, dimmed while it is open), the
    size shortcuts' zero bands, #status-announcement, and the shoe-size unisex fold (moved here from its inline script, which
    never runs after an in-page swap).
  · THE CATEGORY TREE (2026-10-06, snippets/r-cat-tree.liquid): a tick on a department or category ticks everything under it, and
    every parent box shows ticked / part-ticked (a dash) from the paths under it; the folds open and close (and stay as they were
    across the drawer's swaps); the group's summary names a whole category once ('Dresses', not its four styles). Its paths are
    not Search & Discovery values, so the live counts know nothing about them: they are left out of the zeroing, and the SHOW
    total (which Shopify computes with them) is the count. In the desktop RAIL the tree is its own small form: a tick submits it,
    a full page load like every rail link. A page filtered down to nothing can't tell Liquid what was ticked, so the boxes are
    re-ticked from the address.
  NOT IN v1 (DESIGN.md): the sticky bar on scroll-up, dataLayer events, the 800ms section prefetch, desktop rail counts.
  FIX PASS fx6 (2026-10-02, from 4 verifiers): rfacets keys arrive HTML-escaped ("Tod&#39;s") and are decoded before matching;
  every change invalidates older answers synchronously (a superseded or aborted request never patches or falls back to
  'Apply'); long lists re-rank from the live in-stock counts (picked rows pinned, top 8, 'View all n' = the rows it opens)
  whenever the list is not open in its short view; Row C pills on desktop rail pages are full page loads (the rails live
  outside the section and would go stale); no idle prefetch on rail pages; focus kept after View all / Clear all / a shortcut /
  a pill / a sort; Row C never scrolls the page; one number format ('2066', as the theme's own products_count).
*/
(() => {
  if (window.__rFacetsV2) return;
  window.__rFacetsV2 = true;

  const AV = 'filter.v.availability';
  const BAND = 'filter.v.m.custom.size_band';
  /* SHOE SIZE HAS TWO POSSIBLE PARAMS (2026-10-16, Sizing N-4-30): the Search & Discovery "Shoe Size" filter is moving from the
     single-line custom.shoe-size to the list field custom.shoe-size-filter (ranges under every size they span, socks under their shoe
     range, one-size socks under every size). Both are accepted, so the theme can ship before the filter is swapped; shoeParam() is
     whichever one this page's drawer, pills or URL actually uses (the old one when neither is present). */
  const SHOES = ['filter.v.m.custom.shoe-size', 'filter.v.m.custom.shoe-size-filter'];
  const shoeParam = () => SHOES.slice().reverse().find((n) => document.querySelector('[name="' + n + '"], [data-r-p="' + n + '"]')
    || new URLSearchParams(location.search).has(n)) || SHOES[0];
  const NEVER_HIDE = [BAND, 'filter.p.m.product.bag-size', 'filter.p.product_type'];
  const cache = new Map();
  let ctl = null;
  let timer = null;
  let sayTimer = null;
  let token = 0; /* bumped by every change: an answer is used only while its token is still the newest */
  let isOpen = false;
  let committing = false;
  let popClosing = false;
  let ignorePop = 0;
  let focusAfter = false;
  let opening = false; /* the first counts after the drawer opens: zero values are left out, not dimmed */
  let rendered = location.pathname + location.search;

  const drawer = () => {
    const d = document.getElementById('facets-drawer');
    return d && d.hasAttribute('data-r-live-counts') ? d : null;
  };
  const form = () => (drawer() ? document.getElementById('facets-drawer-form') : null);
  const foot = () => {
    const d = drawer();
    return d ? d.querySelector('[data-r-foot]') : null;
  };
  const sectionId = () => {
    const s = document.querySelector('.shopify-section--main-collection, .shopify-section--main-search');
    return s ? s.id.replace('shopify-section-', '') : '';
  };
  const say = (text) => {
    const h = document.getElementById('status-announcement');
    if (!h || !text) return;
    h.textContent = '';
    setTimeout(() => { h.textContent = text; }, 100);
  };
  /* Same format as the theme's own 'collection.products_count' (no thousands separator), so Row B, SHOW and the
     announcements read one way. */
  const fmt = (n) => String(Number(n));
  /* rfacets JSON values are HTML-escaped by Shopify ("Tod&#39;s", "SOIA &amp; KYO"); the inputs hold the decoded value. */
  const decoder = document.createElement('textarea');
  const dec = (s) => {
    s = String(s);
    if (s.indexOf('&') === -1) return s;
    decoder.innerHTML = s;
    return decoder.value;
  };
  /* Desktop rail pages: the rails (layout/theme.liquid) are the entry point; the drawer and Row B are hidden there. */
  const railPage = () => !!document.querySelector('.r-shop-layout') && window.matchMedia('(min-width: 1000px)').matches;

  /* ---------- the form's state ---------- */
  const isSize = (el) => el.name === BAND || SHOES.includes(el.name);
  /* `def` = the state the page was drawn with (what is applied); otherwise what is ticked now (pending). */
  function serialize(f, def) {
    const sp = new URLSearchParams();
    let sized = false;
    for (const el of f.elements) {
      if ((el.type === 'checkbox' || el.type === 'radio') && isSize(el) && (def ? el.defaultChecked : el.checked)) sized = true;
      if (el.hasAttribute('data-r-carry') && isSize(el)) sized = true;
    }
    for (const el of f.elements) {
      if (!el.name || el.type === 'submit' || el.type === 'button' || el.type === 'range') continue;
      /* A filter kept from the address on a page filtered down to nothing (hydrate): always sent as it was. */
      if (el.hasAttribute('data-r-carry')) {
        if (!(el.name === AV && sp.has(AV))) sp.append(el.name, el.value);
        continue;
      }
      if (el.name === AV && el.type === 'hidden') {
        if (sized && sp.has(AV)) continue;
        if (sized) sp.append(AV, '1');
        continue;
      }
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (def ? el.defaultChecked : el.checked) sp.append(el.name, el.value);
        continue;
      }
      const v = def ? el.defaultValue : el.value;
      if (v === '' && (el.name === 'page' || el.name.indexOf('filter.v.price') === 0)) continue;
      sp.append(el.name, v);
    }
    return sp;
  }
  const key = (sp) => [...sp.entries()].map((e) => e[0] + '=' + e[1]).sort().join('&');
  /* `bare` = without the In stock the counts always carry: the TOTAL for a state that will be applied without a size (the
     grid then lists a product that has no variant in stock, 1-7 per page, so the in-stock total would be short by that). */
  function countsUrl(f, def, bare) {
    const d = drawer();
    const sp = serialize(f, def);
    sp.delete('sort_by');
    sp.delete('page');
    sp.delete(AV);
    if (!bare) sp.set(AV, '1');
    const entries = [...sp.entries()].sort((a, b) => (a[0] + '=' + a[1]).localeCompare(b[0] + '=' + b[1]));
    return (d.getAttribute('data-r-path') || f.getAttribute('action')) + '?' + new URLSearchParams(entries).toString() + '&view=rfacets';
  }
  const hasPicks = (f) => [...serialize(f, false).keys()].some((k) => k.indexOf('filter.') === 0 && k !== AV);

  /* ---------- fetching ---------- */
  /* One request per address: a prefetch never aborts anything; a newer pending state aborts the older pending request. */
  const inflight = new Map();
  function get(url, abortable) {
    if (cache.has(url)) return Promise.resolve(cache.get(url));
    if (inflight.has(url)) return inflight.get(url);
    if (abortable && ctl && ctl.url !== url) ctl.abort();
    const c = new AbortController();
    if (abortable) ctl = { url, abort: () => { c.abort(); if (inflight.get(url) === pr) inflight.delete(url); } };
    /* ⚠️ No Accept: application/json header — Shopify would answer with its own collection JSON instead of the template.
       ⚠️ A third-party window.fetch wrapper on the live store answers an ABORTED fetch with {status: 0} instead of rejecting;
       whatever comes back for an aborted request is reported as an AbortError, never as a failure. */
    const pr = fetch(url, { signal: c.signal, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('rfacets ' + r.status))))
      .then((j) => {
        if (!j || typeof j.count !== 'number' || !Array.isArray(j.filters)) throw new Error('rfacets shape');
        cache.set(url, j);
        while (cache.size > 20) cache.delete(cache.keys().next().value);
        return j;
      })
      .catch((err) => {
        if (c.signal.aborted) {
          const a = new Error('aborted');
          a.name = 'AbortError';
          throw a;
        }
        throw err;
      })
      .finally(() => { if (inflight.get(url) === pr) inflight.delete(url); });
    inflight.set(url, pr);
    return pr;
  }
  function prefetch(idle) {
    const f = form();
    if (!f) return;
    if (idle === true && railPage()) return; /* nothing on a rail page reads it (decision 4: no rail counts in v1) */
    const url = countsUrl(f, true);
    if (cache.has(url)) return;
    get(url).then((j) => shortcutZeros(j)).catch(() => {});
  }

  function refresh(ms) {
    const f = form();
    if (!f) return;
    const ft = foot();
    if (ft) ft.setAttribute('aria-busy', 'true');
    /* Synchronously: from this moment no answer for an earlier state may patch the drawer or clear aria-busy. */
    token += 1;
    clearTimeout(timer);
    timer = setTimeout(() => {
      const my = ++token;
      const url = countsUrl(f, false);
      /* The SHOW total must equal the grid after SHOW: unchanged state → the page's own count; with a size → the in-stock
         count (In stock is applied with it); otherwise a second, bare request. */
      const same = key(serialize(f, false)) === key(serialize(f, true));
      const sized = serialize(f, false).has(AV);
      const bare = same || sized ? null : countsUrl(f, false, true);
      Promise.all([get(url, true), bare ? get(bare, false) : null])
        .then(([j, t]) => {
          if (my !== token) return;
          patch(j, same ? pageTotal(j) : t ? t.count : j.count);
        })
        .catch((e) => {
          if (my !== token || (e && e.name === 'AbortError')) return;
          fallback();
        });
    }, ms);
  }
  const pageTotal = (j) => {
    const ft = foot();
    const n = ft ? parseInt(ft.querySelector('[data-r-show]').getAttribute('data-total'), 10) : NaN;
    return isNaN(n) ? j.count : n;
  };

  /* ---------- writing counts into the drawer ---------- */
  function rowOf(inp, label) {
    return inp.closest('.r-fl__row') || inp.closest('.r-fswitch') || label || inp;
  }
  function labelOf(f, inp) {
    return inp.closest('label') || (inp.id ? f.querySelector('label[for="' + CSS.escape(inp.id) + '"]') : null);
  }
  function patch(j, n) {
    const f = form();
    if (!f) return;
    const map = {};
    j.filters.forEach((x) => {
      if (Array.isArray(x.v)) {
        const m = new Map();
        x.v.forEach((t) => m.set(dec(t[0]), t[1]));
        map[x.p] = m;
      }
    });
    const owners = new Map();
    f.querySelectorAll('input[type="checkbox"]').forEach((inp) => {
      if (inp.name.indexOf('filter.') !== 0 || inp.name === AV) return;
      /* Designer rows added past Search & Discovery's 100-value cap (r-facet-checklist `extra`, 2026-10-09, C-45 P2): Shopify has no
         count for them, so rfacets can't either. Leave them as drawn (enabled, no number) instead of reading "no count" as 0. */
      if (inp.hasAttribute('data-r-nocount')) return;
      /* The category tree's raw paths ('>' in them) are not Search & Discovery values: rfacets has no count for them, and 0 would
         disable them. Its other values are (search's groups, an old link's group id): they get counts and dim like any filter
         (2026-10-06 check: on search, groups that dropped to 0 stayed live). */
      if (inp.closest('[data-r-tree]') && inp.value.indexOf('>') !== -1) return;
      const m = map[inp.name];
      const c = m ? m.get(dec(inp.value)) || 0 : 0;
      let owner = inp;
      if (inp.hasAttribute('data-r-ss-hidden')) owner = f.querySelector('input.r-ss-input[data-r-ss="' + inp.getAttribute('data-r-ss') + '"]') || inp;
      owners.set(owner, (owners.get(owner) || 0) + c);
    });
    owners.forEach((n, inp) => {
      const label = labelOf(f, inp);
      const slot = label && label.querySelector('[data-r-fc]');
      if (slot) slot.textContent = fmt(n);
      const li = inp.closest('.r-fl__row');
      if (li) li.setAttribute('data-r-n', n);
      zero(inp, label, n === 0 && !inp.checked);
    });
    f.querySelectorAll('.r-fl[data-r-long]').forEach((ul) => relist(ul, !shortAndOpen(ul)));
    opening = false;
    total(typeof n === 'number' ? n : j.count);
  }
  function zero(inp, label, isZero) {
    const row = rowOf(inp, label);
    const details = inp.closest('details');
    if (!isZero) {
      inp.disabled = false;
      row.classList.remove('r-fz-dim', 'r-fz-hide');
      return;
    }
    inp.disabled = true;
    if (NEVER_HIDE.indexOf(inp.name) !== -1) {
      row.classList.add('r-fz-dim');
    } else if (!opening && details && details.open && !row.classList.contains('r-fz-hide')) {
      row.classList.add('r-fz-dim');
    } else {
      row.classList.remove('r-fz-dim');
      row.classList.add('r-fz-hide');
    }
  }
  function total(n) {
    const ft = foot();
    if (!ft) return;
    const btn = ft.querySelector('[data-r-show]');
    const txt = ft.querySelector('[data-r-show-text]');
    const zeroMsg = ft.querySelector('[data-r-zero]');
    ft.removeAttribute('aria-busy');
    if (n === 0) {
      txt.textContent = btn.getAttribute('data-zero');
      btn.disabled = true;
      zeroMsg.hidden = false;
    } else {
      txt.textContent = (n === 1 ? btn.getAttribute('data-show-one') : btn.getAttribute('data-show-other')).replace('[count]', fmt(n));
      btn.disabled = false;
      zeroMsg.hidden = true;
    }
    clearTimeout(sayTimer);
    if (isOpen) {
      const words = n === 1 ? btn.getAttribute('data-count-one') : btn.getAttribute('data-count-other').replace('[count]', fmt(n));
      sayTimer = setTimeout(() => say(words), 1000);
    }
  }
  /* Counts unavailable: the button stays the theme's 'Apply' and stays usable. */
  function fallback() {
    const ft = foot();
    if (!ft) return;
    ft.removeAttribute('aria-busy');
    const btn = ft.querySelector('[data-r-show]');
    btn.disabled = false;
    ft.querySelector('[data-r-zero]').hidden = true;
    const d = drawer();
    const apply = d && d.getAttribute('data-r-apply');
    if (apply) ft.querySelector('[data-r-show-text]').textContent = apply;
  }

  /* Size shortcuts above the grid: a band with nothing in stock is disabled in place once the counts are known. */
  function shortcutZeros(j) {
    const box = document.querySelector('.r-ftb__sizes');
    if (!box || !j) return;
    const f = j.filters.find((x) => x.p === BAND);
    if (!f || !Array.isArray(f.v)) return;
    const counts = new Map(f.v.map((t) => [dec(t[0]), t[1]]));
    box.querySelectorAll('a.r-ss-box').forEach((a) => {
      if (a.classList.contains('is-active')) return;
      /* Shopify leaves a value with no in-stock product out of the list entirely: missing = 0. */
      const label = a.textContent.trim();
      const off = !counts.has(label) || counts.get(label) === 0;
      a.classList.toggle('r-fz-dim', off);
      if (off) { a.setAttribute('aria-disabled', 'true'); a.setAttribute('tabindex', '-1'); } else { a.removeAttribute('aria-disabled'); a.removeAttribute('tabindex'); }
    });
  }

  /* ---------- summaries and footer ---------- */
  function summaries() {
    const f = form();
    if (!f) return;
    f.querySelectorAll('details').forEach((det) => {
      const out = det.querySelector('[data-r-sum]');
      if (!out) return;
      const tree = det.querySelector('[data-r-tree]');
      if (tree) {
        syncTree(tree);
        out.textContent = treeNames(tree).join(', ');
        return;
      }
      const names = [];
      det.querySelectorAll('input[type="checkbox"]:checked').forEach((inp) => {
        if (inp.hasAttribute('data-r-ss-hidden')) return;
        const label = labelOf(f, inp);
        const t = label && label.querySelector('[data-r-label]');
        if (t) names.push(t.textContent.trim());
      });
      const lo = det.querySelector('input[name="filter.v.price.gte"]');
      const hi = det.querySelector('input[name="filter.v.price.lte"]');
      if (lo && hi && (lo.value !== '' || hi.value !== '')) {
        const pre = det.querySelector('.input-prefix .prefix');
        const sym = pre ? pre.textContent.trim() : '$';
        /* thousands separators, as the pill on the page shows it ('$1,000 - $2,500'; copy check 2026-10-02) */
        const money = (v) => { const x = Number(String(v).replace(/,/g, '')); return isNaN(x) || v === '' ? String(v) : x.toLocaleString('en-US', { maximumFractionDigits: 2 }); };
        names.push(sym + money(lo.value || '0') + ' - ' + sym + money(hi.value || hi.getAttribute('placeholder') || ''));
      }
      out.textContent = names.join(', ');
    });
    const ft = foot();
    if (ft) {
      const clear = ft.querySelector('[data-r-clear]');
      if (clear) {
        const on = hasPicks(f);
        clear.hidden = !on;
        ft.classList.toggle('r-ffoot--two', on);
      }
    }
  }

  /* ---------- folded shoe sizes on the page (copy check 2026-10-02) ----------
     One drawer box can stand for two stored values (on a women's page 'US 6' is both US 6 and Converse's US M4/W6). Shopify
     reports both as applied, so one tap gave two pills ('US 6', 'US M4/W6') and FILTER (2). Merge them: one pill named like the
     box, whose × removes every value it stands for (and In stock with the last size), and FILTER counts the box once. */
  function foldPills() {
    const SHOE = shoeParam();
    const keyOf = new Map();
    const nameOf = new Map();
    document.querySelectorAll('#facets-drawer input[name="' + SHOE + '"][data-r-ss]').forEach((i) => {
      const k = i.getAttribute('data-r-ss');
      keyOf.set(i.value, k);
      if (i.id) {
        const lab = document.querySelector('label[for="' + i.id + '"]');
        const t = lab && lab.querySelector('[data-r-label]');
        if (t) nameOf.set(k, t.textContent.trim());
      }
    });
    if (!keyOf.size) return;
    const groups = new Map();
    document.querySelectorAll('.r-applied a[data-r-p="' + SHOE + '"]').forEach((a) => {
      const k = keyOf.get(a.getAttribute('data-r-v'));
      if (!k) return;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(a);
    });
    let merged = 0;
    groups.forEach((as, k) => {
      const first = as[0];
      const name = nameOf.get(k);
      const v = first.getAttribute('data-r-v');
      if (name && name !== v) {
        const sp = first.querySelector('span');
        if (sp) sp.textContent = name;
        const al = first.getAttribute('aria-label');
        if (al) first.setAttribute('aria-label', al.split(v).join(name));
      }
      if (as.length < 2) return;
      const drop = new Set(as.map((a) => a.getAttribute('data-r-v')));
      const old = first.getAttribute('href') || '';
      const hash = old.indexOf('#') === -1 ? '' : old.slice(old.indexOf('#'));
      const u = new URL(location.href);
      const keep = u.searchParams.getAll(SHOE).filter((x) => !drop.has(x));
      u.searchParams.delete(SHOE);
      keep.forEach((x) => u.searchParams.append(SHOE, x));
      if (!keep.length && !u.searchParams.getAll(BAND).length) u.searchParams.delete(AV);
      u.searchParams.delete('page');
      first.setAttribute('href', u.pathname + u.search + hash);
      as.slice(1).forEach((a) => { const li = a.closest('li'); if (li) li.remove(); merged += 1; });
    });
    if (!merged) return;
    const n = document.querySelector('#r-filter-trigger [data-r-fn]');
    if (n) n.textContent = String(Math.max(0, (parseInt(n.textContent, 10) || 0) - merged));
    const row = document.querySelector('.r-applied');
    const clear = row && row.querySelector('.r-applied__clear');
    if (row && clear && row.querySelectorAll('.r-applied__list > li').length < 2) {
      const box = clear.closest('facet-link') || clear; /* Clear all shows from 2 pills, as in Liquid */
      box.hidden = true;
      box.style.display = 'none';
    }
  }

  /* ---------- long lists: short view, View all, designer search ---------- */
  const fold = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const viewAllOf = (ul) => ul.parentElement.querySelector('[data-r-viewall]');
  function lists(root) {
    (root || document).querySelectorAll('.r-fl[data-r-long]').forEach((ul) => {
      if (!ul.getAttribute('data-r-view')) ul.setAttribute('data-r-view', 'short');
      const b = viewAllOf(ul);
      if (b) b.hidden = ul.getAttribute('data-r-view') !== 'short' || ul.hasAttribute('data-r-nomore');
    });
  }
  /* The list is on screen in its short view: rows must not move under the shopper's finger. */
  const shortAndOpen = (ul) => {
    const det = ul.closest('details');
    return !!(det && det.open) && ul.getAttribute('data-r-view') === 'short';
  };
  /* The short view, recomputed from the LIVE in-stock counts (DESIGN.md "DESIGNER": picked rows pinned first, then the 8 with
     the most in-stock products; "VIEW ALL n" = the rows it opens). The server's rank (Liquid's counts, not in stock) is only
     the tie-break and the order before any count has arrived. `reorder` false: only the 'View all n' number is refreshed. */
  function relist(ul, reorder) {
    const rows = [...ul.children].filter((li) => li.classList && li.classList.contains('r-fl__row'));
    rows.forEach((li) => {
      if (!li.hasAttribute('data-r-srank')) {
        const r = parseFloat(li.style.getPropertyValue('--r-rank'));
        li.setAttribute('data-r-srank', isNaN(r) ? '0' : String(r));
      }
    });
    const open = rows.filter((li) => !li.classList.contains('r-fz-hide'));
    if (reorder) {
      const isPicked = (li) => { const i = li.querySelector('input'); return !!(i && i.checked); };
      const n = (li) => { const v = parseInt(li.getAttribute('data-r-n'), 10); return isNaN(v) ? -1 : v; };
      const sr = (li) => parseFloat(li.getAttribute('data-r-srank')) || 0;
      const cands = open.filter((li) => !isPicked(li)).sort((a, b) => n(b) - n(a) || sr(a) - sr(b));
      rows.forEach((li) => {
        if (isPicked(li)) {
          li.setAttribute('data-r-top', '');
          li.style.setProperty('--r-rank', '-1');
        }
      });
      cands.forEach((li, i) => {
        li.toggleAttribute('data-r-top', i < 8);
        li.style.setProperty('--r-rank', String(i));
      });
      rows.forEach((li) => {
        if (!isPicked(li) && li.classList.contains('r-fz-hide')) li.removeAttribute('data-r-top');
      });
    }
    const shown = rows.filter((li) => li.hasAttribute('data-r-top') && !li.classList.contains('r-fz-hide')).length;
    ul.toggleAttribute('data-r-nomore', open.length <= shown);
    const b = viewAllOf(ul);
    if (b) {
      b.textContent = b.textContent.replace(/\d[\d,]*/, String(open.length));
      if (ul.getAttribute('data-r-view') === 'short') b.hidden = ul.hasAttribute('data-r-nomore');
    }
  }
  function shortView(det) {
    det.querySelectorAll('.r-fl[data-r-long]').forEach((ul) => {
      ul.setAttribute('data-r-view', 'short');
      ul.querySelectorAll('.r-fs-miss').forEach((li) => li.classList.remove('r-fs-miss'));
      relist(ul, true);
    });
    det.querySelectorAll('.r-fsearch__input').forEach((i) => { i.value = ''; });
    det.querySelectorAll('.r-fsearch__clear, [data-r-fempty]').forEach((el) => { el.hidden = true; });
    lists(det);
  }
  function search(input) {
    const box = input.closest('.accordion__content') || input.parentElement.parentElement;
    const ul = box.querySelector('.r-fl');
    if (!ul) return;
    const q = fold(input.value).replace(/[^a-z0-9]+/g, '');
    const clear = box.querySelector('.r-fsearch__clear');
    if (clear) clear.hidden = input.value === '';
    const viewAll = box.querySelector('[data-r-viewall]');
    const empty = box.querySelector('[data-r-fempty]');
    if (q === '') {
      ul.querySelectorAll('.r-fs-miss').forEach((li) => li.classList.remove('r-fs-miss'));
      ul.setAttribute('data-r-view', 'short');
      relist(ul, true); /* a designer ticked from the search is pinned to the top of the short list */
      if (viewAll) viewAll.hidden = ul.hasAttribute('data-r-nomore');
      if (empty) empty.hidden = true;
      return;
    }
    ul.setAttribute('data-r-view', 'search');
    if (viewAll) viewAll.hidden = true;
    let shown = 0;
    ul.querySelectorAll('.r-fl__row').forEach((li) => {
      const words = fold(li.getAttribute('data-name') || '').split(/[^a-z0-9]+/).filter(Boolean);
      const hit = words.some((_, i) => words.slice(i).join('').indexOf(q) === 0);
      li.classList.toggle('r-fs-miss', !hit);
      if (hit && !li.classList.contains('r-fz-hide')) shown += 1;
    });
    if (empty) empty.hidden = shown > 0;
    say(shown === 0 ? 'No designers found' : shown === 1 ? '1 designer' : shown + ' designers');
  }

  /* ---------- the category tree (snippets/r-cat-tree.liquid) ---------- */
  /* A node's own box, the list under it, and the boxes that are real paths (named) inside that list. */
  const nodeBox = (li) => li.querySelector(':scope > .r-ct__row input[type="checkbox"]');
  const nodeSub = (li) => li.querySelector(':scope > .r-ct__sub');
  const pathsIn = (el) => [...el.querySelectorAll('input[type="checkbox"][name]')];
  /* Parents follow what is under them: ticked when every path is, a dash when some are. A department or a category with styles
     is nameless (only real tags filter). Accessories>Hats, Clothing>Vests and Bags>Luggage & Travel are a tag AND have styles:
     their own box is that tag, so it is left as the shopper set it and only gets the dash. Deepest first, so a category's state
     is settled before its department reads it. */
  function syncTree(tree) {
    [...tree.querySelectorAll('li.r-ct__node')].reverse().forEach((li) => {
      const box = nodeBox(li);
      const sub = nodeSub(li);
      if (!box || !sub) return;
      const below = pathsIn(sub);
      const on = below.filter((b) => b.checked).length;
      if (box.name) {
        box.indeterminate = !box.checked && on > 0;
      } else {
        box.checked = below.length > 0 && on === below.length;
        box.indeterminate = on > 0 && on < below.length;
      }
    });
  }
  /* A tick on a parent ticks (or clears) everything under it. */
  function cascade(box) {
    const li = box.closest('li.r-ct__node');
    const sub = li && nodeSub(li);
    if (!sub || nodeBox(li) !== box) return;
    sub.querySelectorAll('input[type="checkbox"]').forEach((b) => {
      b.checked = box.checked;
      b.indeterminate = false;
    });
  }
  /* The group's summary: a node ticked whole is named once; otherwise its ticked children are. */
  function treeNames(tree) {
    const names = [];
    /* A name used in more than one place ('Leather' under Clothing and Accessories, 'Bottoms' under three categories) gets its
       parent in front, 'Accessories: Leather', the 'Group: Value' form the filters use where a value is ambiguous (copy check
       2026-10-06: the summary read 'Leather, Leather'). */
    const seen = new Map();
    tree.querySelectorAll('[data-r-label]').forEach((x) => {
      const t = x.textContent.trim();
      seen.set(t, (seen.get(t) || 0) + 1);
    });
    const named = (li, name) => {
      if ((seen.get(name) || 0) < 2) return name;
      const up = li.parentElement && li.parentElement.closest('li.r-ct__node');
      const ul = up && up.querySelector(':scope > .r-ct__row [data-r-label]');
      return ul ? ul.textContent.trim() + ': ' + name : name;
    };
    const walk = (ul) => {
      [...ul.children].forEach((li) => {
        if (li.hidden) return;
        const box = nodeBox(li);
        const sub = nodeSub(li);
        const label = li.querySelector(':scope > .r-ct__row [data-r-label]');
        const name = label ? label.textContent.trim() : '';
        if (!box) return;
        if (!sub) {
          if (box.checked && name) names.push(named(li, name));
          return;
        }
        const below = pathsIn(sub);
        const all = below.length > 0 && below.every((b) => b.checked) && (!box.name || box.checked);
        if (all) {
          if (name) names.push(named(li, name));
          return;
        }
        if (box.name && box.checked && name) names.push(named(li, name));
        walk(sub);
      });
    };
    walk(tree);
    return names;
  }
  /* Folds: open or closed, remembered by their list's id across the drawer's section swaps (the rail is never swapped). */
  const treeFolds = new Map();
  function twist(btn, open) {
    const sub = document.getElementById(btn.getAttribute('aria-controls'));
    if (!sub) return;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    sub.hidden = !open;
    if (btn.closest('#facets-drawer')) treeFolds.set(sub.id, open);
  }
  function refold() {
    const d = document.getElementById('facets-drawer');
    if (!d) return;
    treeFolds.forEach((open, id) => {
      const btn = d.querySelector('[data-r-twist][aria-controls="' + CSS.escape(id) + '"]');
      if (btn) twist(btn, open);
    });
  }
  /* Liquid reads the ticked paths back from Shopify's own links (r-cat-picks); a page filtered down to nothing has none, so tick
     them from the address there, as applied (defaultChecked). Only raw paths ('>' in them): Search & Discovery values are ticked
     by Liquid already. A path the tree doesn't hold gets its own visible ticked row at the top, named by its last level, as Liquid
     draws one when it can read the picks (2026-10-06 check: it was a hidden box, so nothing showed ticked and only Clear all
     could remove it). */
  function orphanRow(v) {
    const li = document.createElement('li');
    li.className = 'r-ct__node';
    li.innerHTML = '<div class="r-ct__row"><label class="r-ct__label"><input type="checkbox" class="checkbox r-ct__box" name="filter.p.tag">'
      + '<span class="r-ct__name" data-r-label></span></label></div>';
    const box = li.querySelector('input');
    box.value = v;
    box.setAttribute('data-r-key', v);
    box.checked = true;
    box.defaultChecked = true;
    li.querySelector('[data-r-label]').textContent = v.split('>').pop();
    return li;
  }
  /* ON A PAGE WITH NO PRODUCTS THE ADDRESS IS WHAT IS APPLIED. Shopify reports no active filter values there, so neither form held
     the page's other filters (size, colour, price, designer, In stock), and unticking the path that emptied the page dropped them
     too (2026-10-06 check: Balmain women's Yellow = 1 product, + Midi Dresses = 0, untick Midi = all 136 instead of Yellow's 1).
     Each filter in the address the form doesn't hold yet: its box ticked as applied if the form has one, else a hidden input
     (data-r-carry) that serialize() sends as it is and the rail's GET form sends natively. A price is only added when the form has
     no field of that name (the rail writes dollars without commas; the address may not). cats-5: was run only when Liquid could
     not read the ticked paths, which missed a page where it could but still reported no other values. */
  function carry(f, keys) {
    if (!f) return;
    new URLSearchParams(location.search).forEach((v, k) => {
      if (!keys(k)) return;
      const same = [...f.elements].filter((el) => el.name === k);
      if (k.indexOf('filter.v.price') === 0 && same.length) return;
      if (same.some((el) => el.value === v && (el.type === 'hidden' || ((el.type === 'checkbox' || el.type === 'radio') && el.defaultChecked)))) return;
      const box = same.find((el) => (el.type === 'checkbox' || el.type === 'radio') && el.value === v);
      if (box) {
        box.checked = true;
        box.defaultChecked = true;
        return;
      }
      const h = document.createElement('input');
      h.type = 'hidden';
      h.name = k;
      h.value = v;
      h.setAttribute('data-r-carry', '');
      f.appendChild(h);
    });
  }
  function hydrate() {
    const want = new URLSearchParams(location.search).getAll('filter.p.tag').filter((v) => v.indexOf('>') !== -1 && v.charAt(0) !== '_' && v.indexOf('gid:') !== 0);
    if (location.search.indexOf('filter.') !== -1 && !document.querySelector('#main product-card, #main .product-card')) {
      const isFilter = (k) => k.indexOf('filter.') === 0 && k !== 'filter.p.tag';
      document.querySelectorAll('form[data-r-ctform]').forEach((rf) => carry(rf, (k) => isFilter(k) || k === 'sort_by'));
      carry(document.getElementById('facets-drawer-form'), isFilter);
    }
    document.querySelectorAll('[data-r-tree]').forEach((tree) => {
      if (want.length) {
        const boxes = pathsIn(tree);
        if (!boxes.some((b) => b.checked && b.value.indexOf('>') !== -1)) {
          want.forEach((v) => {
            const b = boxes.find((x) => x.value === v);
            if (b) {
              b.checked = true;
              b.defaultChecked = true;
            } else if (!tree.querySelector(':scope > li > .r-ct__row input[data-r-key="' + CSS.escape(v) + '"]')) {
              tree.insertBefore(orphanRow(v), tree.firstChild);
            }
          });
          /* open the folds that hold them */
          pathsIn(tree).filter((b) => b.checked).forEach((b) => {
            let sub = b.closest('.r-ct__sub');
            while (sub) {
              const btn = tree.querySelector('[data-r-twist][aria-controls="' + CSS.escape(sub.id) + '"]');
              if (btn) twist(btn, true);
              sub = sub.parentElement.closest('.r-ct__sub');
            }
          });
        }
      }
      syncTree(tree);
    });
  }

  /* ---------- cancel / commit ---------- */
  function restore(f) {
    for (const el of f.elements) {
      if (el.type === 'checkbox' || el.type === 'radio') el.checked = el.defaultChecked;
      else if (el.type === 'number' || el.type === 'text' || el.type === 'range') el.value = el.defaultValue;
      if (el.type === 'checkbox') el.disabled = false;
    }
    f.querySelectorAll('.r-fz-dim, .r-fz-hide').forEach((el) => el.classList.remove('r-fz-dim', 'r-fz-hide'));
    f.querySelectorAll('[data-r-n]').forEach((el) => el.removeAttribute('data-r-n')); /* pending counts; patch() re-sets */
    if (window.rSyncInStock) window.rSyncInStock(f);
    f.querySelectorAll('details').forEach(shortView);
    summaries();
    const url = countsUrl(f, true);
    token += 1;
    clearTimeout(timer);
    if (cache.has(url)) patch(cache.get(url), pageTotal(cache.get(url)));
    else fallback();
  }

  function commit() {
    const f = form();
    const d = drawer();
    if (!f || !d) return;
    if (key(serialize(f, false)) === key(serialize(f, true))) {
      d.hide();
      return;
    }
    committing = true;
    focusAfter = true;
    const url = new URL(f.getAttribute('action'), location.origin);
    serialize(f, false).forEach((v, k) => url.searchParams.append(k, v));
    url.searchParams.set('section_id', f.closest('facets-form').getAttribute('section-id') || sectionId());
    const scrollTo = f.getAttribute('scroll-to') || '#r-no-scroll';
    Promise.resolve(d.hide()).catch(() => {}).then(() => {
      if (!committing) return; /* dropped: the drawer was reopened before this close finished (dialog:before-show below) */
      committing = false;
      /* After the close: the drawer is back inside the section, so the swap leaves exactly one #facets-drawer-form. */
      document.documentElement.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url, scrollTo } }));
    });
  }

  /* ---------- listeners ---------- */
  document.addEventListener('dialog:before-show', (e) => {
    if (!e.target || e.target.id !== 'facets-drawer' || !drawer()) return;
    isOpen = true;
    opening = true;
    if (committing) {
      /* Reopened before SHOW's close finished (FILTER taps are ignored meanwhile, see the click listener below; a script's
         show() is not): the theme cancels that close and its promise never settles, so the commit is dropped and its picks
         stay pending in the drawer. The drawer's history entry is still the current one, so none is added (fix 2026-10-08,
         C-41: committing stayed true, so X no longer cancelled, the pick was silently lost and each reopen pushed an entry). */
      committing = false;
      focusAfter = false;
    } else {
      try { history.pushState({ rFilters: 1 }, '', location.href); } catch (err) { /* sandboxed */ }
    }
    lists(e.target);
    summaries();
    refresh(0);
  }, true);

  document.addEventListener('dialog:before-hide', (e) => {
    if (!e.target || e.target.id !== 'facets-drawer' || !drawer()) return;
    isOpen = false;
    clearTimeout(sayTimer); /* a pending '206 products' would repeat the one announced after the swap */
    const f = form();
    if (!committing && f) restore(f);
    if (!popClosing && history.state && history.state.rFilters) {
      ignorePop += 1;
      if (committing) ignorePop -= 1; /* the theme replaces the drawer's entry with the new state instead */
      else history.back();
    }
    popClosing = false;
  }, true);

  window.addEventListener('popstate', () => {
    if (ignorePop > 0) {
      ignorePop -= 1;
      return;
    }
    const d = drawer();
    if (isOpen && d) {
      popClosing = true;
      d.hide();
      return;
    }
    const now = location.pathname + location.search;
    if (now === rendered) return;
    const sid = sectionId();
    if (!sid) return;
    const url = new URL(location.href);
    url.hash = '';
    url.searchParams.set('section_id', sid);
    document.documentElement.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url, fromPopstate: true, scrollTo: '#r-no-scroll' } }));
  });

  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!f || f.id !== 'facets-drawer-form' || !drawer()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    commit();
  }, true);

  /* A FILTER tap while SHOW's close (~0.45s) is still running is ignored (fix 2026-10-08, C-41): the theme's show() would cancel
     that close, so the commit waiting on it never ran and the picked filter was silently dropped. Capture on document runs
     before the theme's activator listener on <body>; FILTER and the floating Filters button both carry aria-controls. */
  document.addEventListener('click', (e) => {
    const d = drawer();
    if (!committing || !d || d.open || !e.target || !e.target.closest || !e.target.closest('[aria-controls="facets-drawer"]')) return;
    e.preventDefault();
    e.stopPropagation();
  }, true);

  document.addEventListener('change', (e) => {
    const f = form();
    const t = e.target;
    if (!f || !t || t.form !== f) return;
    if (t.matches && t.matches('input.r-ss-input[data-r-ss]')) {
      f.querySelectorAll('input[data-r-ss-hidden][data-r-ss="' + t.getAttribute('data-r-ss') + '"]').forEach((h) => { h.checked = t.checked; });
    }
    if (t.closest && t.closest('[data-r-tree]')) cascade(t); /* summaries() then re-syncs the parents */
    if (window.rSyncInStock) window.rSyncInStock(f);
    summaries();
    refresh(t.type === 'checkbox' ? 250 : 0);
  });

  document.addEventListener('input', (e) => {
    const t = e.target;
    if (!t) return;
    if (t.classList && t.classList.contains('r-fsearch__input')) {
      search(t);
      return;
    }
    const f = form();
    if (!f || t.form !== f || t.type !== 'number') return;
    summaries();
    refresh(600);
  });

  document.addEventListener('keydown', (e) => {
    const t = e.target;
    if (e.key !== 'Enter' || !t || !t.form || t.form !== form()) return;
    if (t.type === 'number') {
      e.preventDefault();
      refresh(0);
    }
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t || !t.closest) return;
    const tw = t.closest('[data-r-twist]');
    if (tw) {
      e.preventDefault();
      twist(tw, tw.getAttribute('aria-expanded') !== 'true');
      return;
    }
    const va = t.closest('[data-r-viewall]');
    if (va) {
      const ul = va.parentElement.querySelector('.r-fl');
      if (ul) ul.setAttribute('data-r-view', 'all');
      /* Focus moves into the list it opened before the button hides itself (a hidden focused button drops focus to body). */
      const first = ul && [...ul.querySelectorAll('.r-fl__row')].find((li) => !li.classList.contains('r-fz-hide'));
      const inp = first && first.querySelector('input');
      if (inp && document.activeElement === va) inp.focus();
      va.hidden = true;
      return;
    }
    const cs = t.closest('.r-fsearch__clear');
    if (cs) {
      const input = cs.parentElement.querySelector('.r-fsearch__input');
      input.value = '';
      search(input);
      input.focus();
      return;
    }
    const clear = t.closest('[data-r-clear]');
    if (clear) {
      const f = form();
      if (!f) return;
      for (const el of f.elements) {
        if (el.type === 'checkbox' && el.name.indexOf('filter.') === 0) {
          el.checked = false;
          el.disabled = false;
        } else if (el.type === 'number' && el.name.indexOf('filter.v.price') === 0) {
          el.value = '';
        }
      }
      if (window.rSyncInStock) window.rSyncInStock(f);
      const hadFocus = clear.contains(document.activeElement);
      summaries();
      refresh(0);
      /* CLEAR ALL hides itself once nothing is picked: focus goes to SHOW instead of falling to the page. */
      const show = hadFocus && foot() && foot().querySelector('[data-r-show]');
      if (show) {
        show.disabled = false;
        show.focus();
      }
      return;
    }
    const dim = t.closest('.r-ftb__sizes a.r-fz-dim');
    if (dim) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  /* A section closed: back to the short list. A section opened: values that went to 0 meanwhile are left out. */
  document.addEventListener('toggle', (e) => {
    const det = e.target;
    if (!det || det.tagName !== 'DETAILS' || !form() || !form().contains(det)) return;
    if (!det.open) {
      shortView(det);
      return;
    }
    det.querySelectorAll('.r-fz-dim').forEach((row) => {
      const inp = row.querySelector('input') || row;
      if (NEVER_HIDE.indexOf(inp.name) === -1) {
        row.classList.remove('r-fz-dim');
        row.classList.add('r-fz-hide');
      }
    });
    /* Opened: re-rank from the live counts (rows that went to 0 are out, the next ones with stock move up). */
    det.querySelectorAll('.r-fl[data-r-long]').forEach((ul) => relist(ul, true));
  }, true);

  const trigger = (e) => {
    if (e.target && e.target.closest && e.target.closest('#r-filter-trigger')) prefetch();
  };
  document.addEventListener('pointerdown', trigger, true);
  document.addEventListener('focusin', trigger, true);

  /* Every in-page filter change (SHOW, a pill's ×, a size shortcut, a sort, Back): dim the grid while it loads, remember what
     the grid shows, then announce the new count. A link without a hash (the sort sheet) would hand the theme an empty
     selector, which throws; give it one that matches nothing, so nothing scrolls. */
  let loading = false;
  let origin = null; /* which page-level control started the swap, so focus can land on its successor */
  document.addEventListener('facet:update', (e) => {
    const d = e.detail || {};
    if (d.scrollTo === '' || d.scrollTo == null) d.scrollTo = '#r-no-scroll';
    /* FacetLink dispatches on itself, so e.target is the <facet-link>; SHOW and Back dispatch on <html>. */
    const src = e.target && e.target.nodeType === 1 && e.target !== document.documentElement ? e.target : null;
    origin = null;
    if (src && src.closest('.r-applied')) {
      /* A pill's × or Clear all: no scroll (DESIGN.md "PAGE-LEVEL ACTIONS"); the '#collection' hash stays only for no-JS. */
      d.scrollTo = '#r-no-scroll';
      const chips = [...src.closest('.r-applied').querySelectorAll('.r-applied__list facet-link')];
      origin = chips.indexOf(src) === -1 ? { k: 'clear' } : { k: 'chip', i: chips.indexOf(src) };
    } else if (src && src.closest('.r-ftb__sizes')) {
      origin = { k: 'box', label: src.textContent.trim() };
    } else if (src && src.closest('#r-sort-popover')) {
      origin = { k: 'sort' };
    }
    if (d.url) {
      const u = new URL(d.url, location.origin);
      u.searchParams.delete('section_id');
      if (d.ignoreUrlSearch) u.search = '';
      rendered = u.pathname + u.search;
    }
    loading = true;
    document.documentElement.classList.add('r-f-loading');
  }, true);
  document.addEventListener('theme:loading:end', () => {
    if (!loading) return;
    loading = false;
    setTimeout(() => {
      document.documentElement.classList.remove('r-f-loading');
      foldPills();
      lists(document);
      remember();
      refold();
      hydrate();
      const c = document.querySelector('.r-ftb__count');
      if (c) say(c.textContent.trim());
      /* After SHOW, focus goes back to FILTER (stable id across the swap); Safari never focuses a tapped button itself. */
      if (focusAfter) {
        focusAfter = false;
        origin = null;
        const ft = document.getElementById('r-filter-trigger');
        if (ft) ft.focus({ preventScroll: true });
      } else if (origin) {
        refocus(origin);
        origin = null;
      }
      const f = form();
      if (f) {
        const url = countsUrl(f, true);
        if (cache.has(url)) shortcutZeros(cache.get(url));
        else if ('requestIdleCallback' in window) requestIdleCallback(() => prefetch(true), { timeout: 2000 });
        else setTimeout(() => prefetch(true), 300);
      }
    }, 0);
  });

  /* After a shortcut, a pill or a sort the control that was used is gone (the section was swapped); focus goes to its
     successor instead of <body>: the same size box, the next pill (or Clear all, or FILTER), the SORT pill. */
  function refocus(o) {
    const ae = document.activeElement;
    if (ae && ae !== document.body && ae.isConnected) return;
    let el = null;
    if (o.k === 'box') {
      el = [...document.querySelectorAll('.r-ftb__sizes a.r-ss-box')].find((a) => a.textContent.trim() === o.label) || null;
    } else if (o.k === 'chip') {
      const chips = document.querySelectorAll('.r-applied__list a.r-chip');
      el = chips[Math.min(o.i, chips.length - 1)] || document.querySelector('.r-applied__clear');
    } else if (o.k === 'sort') {
      el = document.getElementById('r-sort-trigger');
    }
    el = el || document.getElementById('r-filter-trigger');
    if (el && el.offsetParent !== null) el.focus({ preventScroll: true });
  }

  /* DESKTOP RAIL PAGES (1000px+, .r-shop-layout): a Row C pill or Clear all is a full page load, like every rail link. An
     in-page swap redraws only the section, and the rails (layout/theme.liquid) would keep the removed filter — still drawn
     as picked and carried by every rail link, so the next rail click brought it back (verifier D1-desktop). Runs before the
     <facet-link>'s own listener (capture on document); modified clicks (new tab) keep the browser's default. */
  document.addEventListener('click', (e) => {
    const a = e.target && e.target.closest ? e.target.closest('.r-applied a[href]') : null;
    if (!a || !a.closest('.r-shop-layout') || !window.matchMedia('(min-width: 1000px)').matches) return;
    e.stopPropagation();
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    location.assign(a.href.split('#')[0]);
  }, true);

  /* The shoe-size fold (r-shoe-size-grid) for pages where its inline script never ran (a grid that arrived by section swap). */
  window.__rShoeFold = true;

  /* The drawer's server-rendered label ('Apply'): what the button falls back to when counts can't be had. */
  function remember() {
    const d = drawer();
    const t = d && d.querySelector('[data-r-show-text]');
    if (t && !d.hasAttribute('data-r-apply')) d.setAttribute('data-r-apply', t.textContent.trim());
  }
  /* THE TREE IN THE DESKTOP RAIL (r-cat-tree mode 'rail'): its own GET form, outside the drawer. A tick (on a parent: everything
     under it) submits at once: a full page load, like every rail link; the grid dims meanwhile. */
  /* A KEYBOARD tick reloads the page, which would drop focus at the top: remember the box, and focus it again after the load
     (copy check 2026-10-06). Not after a click: a mouse user would just get a focus ring on the box. */
  let pointerTick = false;
  document.addEventListener('pointerdown', () => { pointerTick = true; }, true);
  document.addEventListener('keydown', () => { pointerTick = false; }, true);
  const CT_FOCUS = 'r-ct-focus';
  function ctRefocus() {
    let key = null;
    try { key = sessionStorage.getItem(CT_FOCUS); sessionStorage.removeItem(CT_FOCUS); } catch (err) { /* storage blocked */ }
    if (!key) return;
    const box = [...document.querySelectorAll('form[data-r-ctform] input[data-r-key]')].find((b) => b.getAttribute('data-r-key') === key);
    if (box) box.focus();
  }
  document.addEventListener('change', (e) => {
    const t = e.target;
    const rf = t && t.form;
    if (!rf || !rf.hasAttribute('data-r-ctform') || t.type !== 'checkbox') return;
    if (!pointerTick && t.hasAttribute('data-r-key')) {
      try { sessionStorage.setItem(CT_FOCUS, t.getAttribute('data-r-key')); } catch (err) { /* storage blocked */ }
    }
    cascade(t);
    const tree = rf.querySelector('[data-r-tree]');
    if (tree) syncTree(tree);
    document.documentElement.classList.add('r-ct-loading');
    /* Nothing left to send (the last path unticked on a page with nothing else on): the page itself, not 'page?' (2026-10-06 check). */
    if ([...new FormData(rf).keys()].length === 0) {
      location.href = rf.action;
      return;
    }
    if (rf.requestSubmit) rf.requestSubmit();
    else rf.submit();
  });
  /* Back to a page from the browser's cache: the boxes the shopper had ticked on the way out may still show; put them back to what
     the page actually shows, and undim it. */
  window.addEventListener('pageshow', (e) => {
    document.documentElement.classList.remove('r-ct-loading');
    if (!e.persisted) return;
    document.querySelectorAll('form[data-r-ctform]').forEach((rf) => {
      rf.querySelectorAll('input[type="checkbox"]').forEach((b) => { b.checked = b.defaultChecked; });
      const tree = rf.querySelector('[data-r-tree]');
      if (tree) syncTree(tree);
    });
  });

  const start = () => {
    hydrate();
    ctRefocus();
    foldPills();
    lists(document);
    remember();
    if ('requestIdleCallback' in window) requestIdleCallback(() => prefetch(true), { timeout: 3000 });
    else setTimeout(() => prefetch(true), 1200);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
